/**
 * Force Protection Service
 * Baseline scan / trusted-device review / watchlist management for DFP, scoped
 * to COP geofences. Talks to the PathfinderRF baseline + admin endpoints.
 */

export interface BaselineDevice {
  signal_type: string;
  stable_identifier: string;
  normalized_identifier: string;
  identifier?: string | null;
  display_name?: string | null;
  manufacturer?: string | null;
  device_category?: string | null;
  is_la_bit: boolean;
  is_tracker: boolean;
  sighting_count: number;
  distinct_locations: number;
  max_rssi?: number | null;
  first_seen?: string | null;
  last_seen?: string | null;
  already_trusted: boolean;
  on_watchlist: boolean;
}

export interface BaselineScanResponse {
  geofence_id?: number | null;
  start?: string | null;
  end?: string | null;
  device_count: number;
  devices: BaselineDevice[];
}

export interface BaselineSession {
  id: number;
  geofence_id: number;
  label?: string | null;
  started_at: string;
  ended_at?: string | null;
  status: string;
}

export interface TrustedDevice {
  id: number;
  identifier: string;
  normalized_identifier: string;
  identifier_type: string;
  label?: string | null;
  reason?: string | null;
  applies_to: string;
  enabled: boolean;
  geofence_id?: number | null;
  last_reviewed_at?: string | null;
}

export interface WatchlistEntry {
  id: number;
  identifier?: string | null;
  normalized_identifier?: string | null;
  identifier_type: string;
  match_mode: string;
  pattern?: string | null;
  signal_type: string;
  geofence_id?: number | null;
  severity_floor: string;
  label?: string | null;
  reason?: string | null;
  enabled: boolean;
}

export interface BulkApproveItem {
  identifier: string;
  identifier_type?: string;
  label?: string;
  applies_to?: string;
}

class ForceProtectionService {
  private baseUrl = process.env.REACT_APP_API_URL || '/api';

  private headers() {
    return {
      Authorization: `Bearer ${localStorage.getItem('access_token') || ''}`,
      'Content-Type': 'application/json',
    };
  }

  private async req<T>(path: string, init?: RequestInit): Promise<T> {
    const resp = await fetch(`${this.baseUrl}${path}`, { headers: this.headers(), ...init });
    if (!resp.ok) {
      const text = await resp.text().catch(() => '');
      throw new Error(`HTTP ${resp.status}${text ? `: ${text}` : ''}`);
    }
    return resp.json();
  }

  // ---- Baseline ----
  async baselineScan(params: {
    geofence_id?: number;
    polygon?: any;
    start?: string;
    end?: string;
    signal_types?: string[];
    min_rssi?: number;
    min_sightings?: number;
    exclude_cotravelers?: boolean;
  }): Promise<BaselineScanResponse> {
    return this.req<BaselineScanResponse>('/dfp/baseline/scan', {
      method: 'POST',
      body: JSON.stringify(params),
    });
  }

  async baselineDiff(geofenceId: number, signalTypes?: string[]): Promise<BaselineScanResponse> {
    const q = new URLSearchParams({ geofence_id: String(geofenceId) });
    if (signalTypes && signalTypes.length) q.append('signal_types', signalTypes.join(','));
    return this.req<BaselineScanResponse>(`/dfp/baseline/diff?${q}`);
  }

  async listSessions(geofenceId?: number): Promise<BaselineSession[]> {
    const q = new URLSearchParams();
    if (geofenceId != null) q.append('geofence_id', String(geofenceId));
    return this.req<BaselineSession[]>(`/dfp/baseline/sessions?${q}`);
  }

  async startSession(geofenceId: number, label?: string): Promise<BaselineSession> {
    return this.req<BaselineSession>('/dfp/baseline/sessions', {
      method: 'POST',
      body: JSON.stringify({ geofence_id: geofenceId, label }),
    });
  }

  async stopSession(sessionId: number): Promise<BaselineSession> {
    return this.req<BaselineSession>(`/dfp/baseline/sessions/${sessionId}/stop`, { method: 'POST' });
  }

  // ---- Trusted devices (exclusions) ----
  async listTrusted(): Promise<TrustedDevice[]> {
    return this.req<TrustedDevice[]>('/admin/dfp/exclusions');
  }

  async bulkApprove(params: {
    devices: BulkApproveItem[];
    geofence_id?: number;
    scan_session_id?: number;
    reason?: string;
    applies_to?: string;
  }): Promise<{ success: boolean; approved: number; skipped: any[] }> {
    return this.req('/admin/dfp/exclusions/bulk', {
      method: 'POST',
      body: JSON.stringify(params),
    });
  }

  async deleteTrusted(id: number): Promise<void> {
    await this.req(`/admin/dfp/exclusions/${id}`, { method: 'DELETE' });
  }

  // ---- Watchlist ----
  async listWatchlist(): Promise<WatchlistEntry[]> {
    return this.req<WatchlistEntry[]>('/admin/dfp/watchlist');
  }

  async createWatchlist(entry: Partial<WatchlistEntry>): Promise<WatchlistEntry> {
    return this.req<WatchlistEntry>('/admin/dfp/watchlist', {
      method: 'POST',
      body: JSON.stringify(entry),
    });
  }

  async deleteWatchlist(id: number): Promise<void> {
    await this.req(`/admin/dfp/watchlist/${id}`, { method: 'DELETE' });
  }
}

export const forceProtectionService = new ForceProtectionService();
